import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { AuthThrottleService } from "../../../../../lib/mfa/features/mfa/services/auth-throttle.service";
import {
  ConfirmNewPhoneNumberRequest,
  ConfirmNewPhoneNumberResponse
} from "../models/customer.model";
import { CustomerRemote } from "../../infrastructure/customer.remote";
import { MfaErrorType } from "../../../../../lib/mfa/features/mfa/const/enums/mfa-error-type.enum";
import { MfaErrorCodeType } from "../../../../../lib/mfa/features/mfa/const/enums/mfa-error-code-type.enum";
import { MfaService } from "../../../../../lib/mfa/features/mfa/services/mfa.service";
import { MessageService } from "../../../../message/message.service";
import { MfaApiResponse } from "../../../../../lib/mfa/features/mfa/const/models/mfa-api.models";
import { InitMfaData } from "../../../../../lib/mfa/features/mfa/const/models/init-mfa-data.model";
import { MfaContextType } from "../../../../../lib/mfa/features/mfa/const/enums/mfa-context-type.enum";
import { AppPageService } from "../../../../../lib/ui/app/app-page/app-page.service";

@Injectable({
  providedIn: "root",
})
export class CustomerActionsService {
  constructor(
    private readonly remote: CustomerRemote,
    private readonly messageService: MessageService,
    private readonly mfaService: MfaService,
    private readonly router: Router,
    private readonly appPageService: AppPageService,
    private readonly authThrottleService: AuthThrottleService
  ) {}

  redirectToDashboard() {
    if(!this.appPageService.backToIfPossible(true))
    {
      // TODO[ids] refactor needed (temp solution with hard redirect to dashboard)
      this.router.routeReuseStrategy.shouldReuseRoute = () => false;
      this.router.onSameUrlNavigation = "reload";
      this.router.navigate(["/dashboard"]);
    }
  }

  confirmMobilePhoneNumberChange(
    request: ConfirmNewPhoneNumberRequest
  ): Observable<any> {
    return this.remote
      .confirmMobilePhoneNumberChange(request)
      .pipe(
        catchError((error) =>
          this.handleConfirmNewPhoneNumberError(request, error)
        )
      );
  }

  private handleConfirmNewPhoneNumberError(
    request: ConfirmNewPhoneNumberRequest,
    response: any
  ): Observable<ConfirmNewPhoneNumberResponse> {
    const throttle = this.authThrottleService.handle(response);
    if (throttle) {
      return of({ hasError: true, errorCode: throttle });
    }

    if (response.status === 403 && this.hasMfaError(response?.error?.error)) {
      return this.handleMfaErrors(request, response?.error);
    }

    this.messageService.error(
      "Error occurred while trying to verify new phone number"
    );
    return of({ hasError: true });
  }

  private hasMfaError(errorType: any) {
    return Object.values(MfaErrorType).includes(errorType);
  }

  private handleMfaErrors(
    request: ConfirmNewPhoneNumberRequest,
    mfaResponse: MfaApiResponse
  ) {
    switch (mfaResponse.error) {
      case MfaErrorType.MfaRequired:
        return this.handleMfaRequiredError(request, mfaResponse);
      case MfaErrorType.MfaCodeError:
        return this.handleMfaCodeError(mfaResponse);
      default:
        console.error(`Mfa error: ${mfaResponse.error}`);
        return of({ hasError: true });
    }
  }

  private handleMfaRequiredError(
    request: ConfirmNewPhoneNumberRequest,
    mfaResponse: MfaApiResponse
  ) {
    const data: InitMfaData = {
      mfaContextType: MfaContextType.ChangePhoneNumber,
      mfaResponse,
      contextData: {
        customerId: request.customerId,
        oldPhoneNumber: request.oldPhoneNumber,
      },
    };
    this.mfaService.initMfa(data);
    return of({ hasError: true });
  }

  private handleMfaCodeError(mfaResponse: MfaApiResponse) {
    if (
      mfaResponse?.send_code_status?.error_result?.error ===
      MfaErrorCodeType.InvalidMfaCode
    ) {
      return of({ hasError: true, errorCode: MfaErrorCodeType.InvalidMfaCode });
    }
  }
}
